import React, { Component } from 'react';
import axios from '../../../api/axios';
import ReactSelect from '../../elements/ReactSelect';
import ModalBody from '../modal/ModalBody';
import ModalControls from '../modal/ModalControls';
import ModalFooter from '../modal/ModalFooter';
import ModalHeader from '../modal/ModalHeader';


// Define the option type for the stats
export interface MarketingStatsOption {
  value: string;
  label: string;
}

type Props = {
  onCloseModal: () => void;
  disableButtons?: boolean;
};

type State = {
  selectedOptions: Set<string | unknown>;
  isLoading: boolean;
  isFetchingOptions: boolean;
  isDropdownOpen: boolean;
  error: string | null;
  availableOptions: MarketingStatsOption[];
};

class MarketingStatsModal extends Component<Props, State> {
  private defaultOptions: MarketingStatsOption[] = [
    {
      value: 'Option 1',
      label: 'Option 1',
    },
    {
      value: 'Option 2',
      label: 'Option 2',
    },
    {
      value: 'Option 3',
      label: 'Option 3',
    },
  ];

  constructor(props: Props) {
    super(props);
    this.state = {
      selectedOptions: new Set(),
      isLoading: false,
      isFetchingOptions: false,
      error: null,
      availableOptions: this.defaultOptions,
      isDropdownOpen: false,
    };
    this.handleToggleDropdown = this.handleToggleDropdown.bind(this);
  }

  handleToggleDropdown = () => {
    this.setState(prevState => ({
      isDropdownOpen: !prevState.isDropdownOpen,
    }));
  };

  componentDidMount() {
    this.fetchOptions();
  }

  fetchOptions = async () => {
    this.setState({ isFetchingOptions: true, error: null });

    try {
      const response = await axios.get('/api/marketing/statistics/options');

      if (response.status === 200 && response.data) {
        const optionsData = response.data.options || response.data;
        const selectAllOption = { label: 'Select All', value: 'SELECT_ALL' };
        const extendedData = [selectAllOption, ...optionsData];

        if (Array.isArray(optionsData)) {
          this.setState({ availableOptions: extendedData, isFetchingOptions: false });
        } else {
          throw new Error('Invalid options format from backend');
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to load options';
      this.setState({
        availableOptions: this.defaultOptions,
        isFetchingOptions: false,
        error: errorMessage,
      });
    }
  };

  isFormValid = (): boolean => {
    const { selectedOptions } = this.state;

    return selectedOptions.size > 0;
  };

  handleSubmit = async () => {
    // Leave the modal open
    // const { onCloseModal } = this.props;
    const { selectedOptions } = this.state;
    const exportUrl = '/api/marketing/statistics/export';

    if (!this.isFormValid()) {
      this.setState({ error: 'Please select at least one option' });

      return;
    }

    this.setState({ isLoading: true, error: null });

    try {
      const payload = {
        selected_options: Array.from(selectedOptions)
      };

      const response = await axios.post(exportUrl, payload, {
        responseType: 'blob',
      });

      if (response.status === 200 || response.status === 201) {

        const contentDisposition = response.headers['content-disposition'];
        let filename = 'experiments-analytics.xlsx';

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="([^"]+)"/i);
          if (filenameMatch && filenameMatch.length > 1) {
            filename = filenameMatch[1];
          }
        }

        const blob = new Blob([response.data], { type: response.data.type });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);

        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url); // Clean up the temporary URL

        this.resetForm();
        // Leave the modal open
        // onCloseModal();
      }
    } catch (error: any) {
      let errorMessage = 'An error occurred during export. Please try again.';

      if (error.response && error.response.data instanceof Blob) {
        const errorText = await error.response.data.text();
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch {
          // Failed to parse, use generic message
        }
      } else {
        errorMessage = error.response?.data?.error || errorMessage;
      }

      this.setState({ error: errorMessage });

    } finally {
      this.setState({ isLoading: false });
    }
  };

  resetForm = () => {
    this.setState({
      selectedOptions: new Set(),
      isLoading: false,
      error: null,
    });
  };

  handleClose = () => {
    const { onCloseModal } = this.props;

    this.resetForm();
    onCloseModal();
  };

  handleSelectOptionChange = (data: any | null) => {
    const selectedData = data || [];
    const { availableOptions } = this.state;
    const wasSelectAllClicked = selectedData.some((o: any) => o.value === 'SELECT_ALL');

    if (wasSelectAllClicked) {
      const actualOptions = availableOptions.filter(o => o.value !== 'SELECT_ALL');
      this.setState({
        selectedOptions: new Set(actualOptions.map(o => o.value)),
        error: null
      });
    } else {
      const newSelection = new Set(
        selectedData
          .filter((o: any) => o.value !== 'SELECT_ALL')
          .map((o: any) => o.value)
      );

      this.setState({
        selectedOptions: newSelection,
        error: null
      });
    }
  };

  render(): React.ReactNode {
    const { disableButtons } = this.props;
    const {
      isLoading,
      error,
      availableOptions,
      selectedOptions,
    } = this.state;

    return (
      <>
        <div className="marketing-statistics">
          <ModalHeader title="Marketing Statistics" onCloseClick={this.handleClose} />
          <ModalBody>
            <div className="marketing-stats-modal-content">
              {isLoading && (
                <div className="loading-overlay">
                  <div className="loading-spinner">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="loading-text">Processing...</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {error}
                  <button
                    type="button"
                    className="close"
                    data-dismiss="alert"
                    aria-label="Close"
                    onClick={() => this.setState({ error: null })}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              )}

              <ReactSelect
                id="optionIds"
                name="optionIds[]"
                label="Tracking ID"
                defaultValue={[]}
                value={availableOptions.filter(o => selectedOptions.has(o.value))}
                options={availableOptions}
                onChange={this.handleSelectOptionChange}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <ModalControls
              showEditButton={false}
              disableButtons={disableButtons || isLoading}
              onCloseButtonClick={this.handleClose}
              onSubmitButtonClick={this.handleSubmit}
              submitButtonText="Export"
              isFormValid={!this.isFormValid()}
            />
          </ModalFooter>
        </div>
      </>
    );
  }
}

export default MarketingStatsModal;